import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Input } from './ui/input';
import { User, Mail, Phone } from 'lucide-react';

const ContactAutocomplete = ({ 
  value, 
  onChange, 
  onSelectContact,
  placeholder = "Start typing client name...",
  required = false,
  dataTestId = "contact-autocomplete"
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch all contacts on mount
  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const response = await axios.get(`${API}/contacts`);
        setContacts(response.data);
      } catch (error) {
        console.error('Failed to load contacts:', error);
      }
    };
    fetchContacts();
  }, []);

  // Update input when value prop changes
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filterContacts = (query) => {
    if (!query || query.length < 1) {
      return [];
    }
    
    const lowerQuery = query.toLowerCase();
    return contacts.filter(contact => {
      const nameMatch = contact.name?.toLowerCase().includes(lowerQuery);
      const emailMatch = contact.email?.toLowerCase().includes(lowerQuery);
      const phoneMatch = contact.phone?.includes(query);
      return nameMatch || emailMatch || phoneMatch;
    }).slice(0, 8); // Limit to 8 suggestions
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setHighlightedIndex(-1);
    
    // Filter contacts locally
    const filtered = filterContacts(newValue);
    setSuggestions(filtered);
    setShowSuggestions(filtered.length > 0);
    
    // Also trigger the parent onChange with just the name
    if (onChange) {
      onChange({
        target: {
          name: 'client_name',
          value: newValue
        }
      });
    }
  };

  const handleSelectContact = (contact) => {
    setInputValue(contact.name);
    setShowSuggestions(false);
    setSuggestions([]);
    
    // Call the callback to fill in all contact details
    if (onSelectContact) {
      onSelectContact(contact);
    }
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
        if (highlightedIndex >= 0 && suggestions[highlightedIndex]) {
          e.preventDefault();
          handleSelectContact(suggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
      default:
        break;
    }
  };

  const handleFocus = () => {
    if (inputValue && suggestions.length > 0) {
      setShowSuggestions(true);
    } else if (inputValue) {
      const filtered = filterContacts(inputValue);
      if (filtered.length > 0) {
        setSuggestions(filtered);
        setShowSuggestions(true);
      }
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <Input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        placeholder={placeholder}
        required={required}
        autoComplete="off"
        data-testid={dataTestId}
        className="form-input"
      />
      
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
          <div className="p-2 text-xs text-gray-500 border-b bg-gray-50">
            Select a contact to auto-fill details
          </div>
          {suggestions.map((contact, index) => (
            <div
              key={contact.id}
              className={`px-3 py-2 cursor-pointer transition-colors ${
                index === highlightedIndex 
                  ? 'bg-blue-50 border-l-2 border-blue-500' 
                  : 'hover:bg-gray-50'
              }`}
              onClick={() => handleSelectContact(contact)}
              data-testid={`contact-suggestion-${index}`}
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                  <User className="w-4 h-4 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{contact.name}</p>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    {contact.email && (
                      <span className="flex items-center gap-1 truncate">
                        <Mail className="w-3 h-3" />
                        {contact.email}
                      </span>
                    )}
                    {contact.phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {contact.phone}
                      </span>
                    )}
                  </div>
                </div>
                {contact.total_rides > 0 && (
                  <span className="text-xs text-gray-400">
                    {contact.total_rides} rides
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContactAutocomplete;
